import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import SettingsPage from './components/SettingsPage';
import UserManagement from './components/UserManagement';
import ApiKeyManagement from './components/ApiKeyManagement';
import CompanyManagement from './components/CompanyManagement';
import DatasetDetailPage from './components/data-sources/DatasetDetailPage';
import MetricDetailPage from './components/data-sources/MetricDetailPage';
import AddDataSourcePage from './components/data-sources/AddDataSourcePage';
import PackPage from './components/data-sources/PackPage';
import ConnectionCallbackPage from './components/data-sources/ConnectionCallbackPage';
import PacksPage from './components/data-sources/PacksPage';
import DatasetDefinitionPage from './components/data-sources/DatasetDefinitionPage';
import KpiManagement from './components/KpiManagement';
import AlertsPage from './components/AlertsPage';
import StatementPage from './components/StatementPage';
import KpiPage from './components/KpiPage';
import ReportsPage from './components/ReportsPage';
import ReportPage from './components/ReportPage';
import DataSourcesPage from './components/DataSourcesPage';
import UserProfile from './components/UserProfile';
import Sidebar from './components/Sidebar';
import AssistantPanel from './components/assistant/AssistantPanel';
import useStore from './store/useStore';
import { authAPI, companiesAPI } from './services/api';
import websocketService from './services/websocket';
import { loadDashboardData } from './utils/loadDashboardData';
import { useDashboardStale } from './hooks/useDashboardStale';

// CRITICAL: Define route protection components OUTSIDE App to prevent recreation on every render
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = useStore((state) => state.isAuthenticated);
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

const ProtectedAdminRoute = ({ children }) => {
  const isAuthenticated = useStore((state) => state.isAuthenticated);
  const user = useStore((state) => state.user);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.role !== 'admin' && user?.role !== 'super_admin') return <Navigate to="/" replace />;
  return children;
};

const ProtectedSuperAdminRoute = ({ children }) => {
  const isAuthenticated = useStore((state) => state.isAuthenticated);
  const user = useStore((state) => state.user);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.role !== 'super_admin') return <Navigate to="/" replace />;
  return children;
};

function App() {
  const isAuthenticated = useStore((state) => state.isAuthenticated);
  const token = useStore((state) => state.token);
  const currentDashboardId = useStore((state) => state.currentDashboardId);
  const currentCompany = useStore((state) => state.currentCompany);

  // A session started before companies had a currency remembers the company without one.
  useEffect(() => {
    if (!isAuthenticated || !currentCompany?.id || currentCompany.currency) return;
    companiesAPI.getById(currentCompany.id)
      .then((res) => {
        const currency = res?.data?.currency;
        const latest = useStore.getState().currentCompany;
        if (!currency || latest?.id !== currentCompany.id) return;
        const updated = { ...latest, currency };
        localStorage.setItem('currentCompany', JSON.stringify(updated));
        useStore.setState({ currentCompany: updated });
      })
      .catch(() => {});
  }, [isAuthenticated, currentCompany]);

  // The theme belongs to the account: pick up a change made on another browser, or before this was stored.
  useEffect(() => {
    if (!isAuthenticated) return;
    const themeWhenAsked = useStore.getState().user?.theme;
    authAPI.getCurrentUser()
      .then((res) => {
        const theme = res?.data?.user?.theme;
        const current = useStore.getState().user?.theme;
        // If the user chose a theme while this was on its way, this answer is out of date.
        if (current !== themeWhenAsked) return;
        if (theme && theme !== current) useStore.getState().setTheme(theme);
      })
      .catch(() => {});
  }, [isAuthenticated]);

  const updateMetric = useStore((state) => state.updateMetric);
  const setWsConnected = useStore((state) => state.setWsConnected);
  const setLastUpdate = useStore((state) => state.setLastUpdate);

  const isStale = useDashboardStale();

  // Initialize data and WebSocket connection
  useEffect(() => {
    if (!isAuthenticated) return;

    // Load dashboard and metrics in parallel via shared utility
    if (currentCompany?.id) {
      loadDashboardData({
        companyId: currentCompany.id,
        dashboardId: currentDashboardId ? parseInt(currentDashboardId) : null,
      }).catch(error => {
        console.error('Error loading initial data:', error);
      });
    }

    // Connect WebSocket
    websocketService.connect(token);

    // Handle incoming metric updates and alert transitions
    const handleMetricUpdate = (message) => {
      if (message.type === 'alert') {
        const currentStoreCompany = useStore.getState().currentCompany;
        if (currentStoreCompany && message.data?.companyId === currentStoreCompany.id) {
          useStore.getState().applyAlertEvent(message.data);
        }
        return;
      }
      if (message.type === 'metric_update') {
        const { data } = message;

        // Get current company from store (not closure) to handle company switches
        const currentStoreCompany = useStore.getState().currentCompany;

        // Only update if this metric belongs to the current company
        if (currentStoreCompany && data.companyId === currentStoreCompany.id) {
          // Update metric (including service status which is now just a regular metric)
          updateMetric(data.metricKey, data);

          // Use the actual metric timestamp, not the current time
          if (data.timestamp) {
            setLastUpdate(new Date(data.timestamp));
          }
        }
      }
    };

    // Handle WebSocket connection status changes
    const handleConnectionStatusChange = (isConnected) => {
      setWsConnected(isConnected);
    };

    // Subscribe to WebSocket events
    const unsubscribeFromMessages = websocketService.onMessage(handleMetricUpdate);
    const unsubscribeFromConnectionChanges = websocketService.onConnectionChange(handleConnectionStatusChange);

    // Cleanup on unmount
    return () => {
      unsubscribeFromMessages();
      unsubscribeFromConnectionChanges();
      websocketService.disconnect();
    };
  }, [isAuthenticated, token]);

  return (
    <BrowserRouter>
      <div className={`app ${isAuthenticated ? 'app-with-sidebar' : ''} ${isStale ? 'app-stale' : ''}`}>
        {isAuthenticated && <Sidebar />}
        {isAuthenticated && <AssistantPanel />}
        <div className="main-content">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <UserProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedAdminRoute>
                  <UserManagement />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/api-keys"
              element={
                <ProtectedAdminRoute>
                  <ApiKeyManagement />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <ProtectedRoute>
                  <ReportsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/ready/:datasetName/:reportKey"
              element={
                <ProtectedRoute>
                  <ReportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/:id"
              element={
                <ProtectedRoute>
                  <ReportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/kpis/:id"
              element={
                <ProtectedRoute>
                  <KpiPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/statements/:datasetName/:layoutKey"
              element={
                <ProtectedRoute>
                  <StatementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/alerts"
              element={
                <ProtectedRoute>
                  <AlertsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/kpis"
              element={
                <ProtectedAdminRoute>
                  <KpiManagement />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/data-sources"
              element={
                <ProtectedAdminRoute>
                  <DataSourcesPage />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/data-sources/add"
              element={
                <ProtectedAdminRoute>
                  <AddDataSourcePage />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/data-sources/connections/:connector/callback"
              element={
                <ProtectedAdminRoute>
                  <ConnectionCallbackPage />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/data-sources/packs"
              element={
                <ProtectedAdminRoute>
                  <PacksPage />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/data-sources/datasets/new"
              element={
                <ProtectedAdminRoute>
                  <DatasetDefinitionPage />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/data-sources/datasets/:name/edit"
              element={
                <ProtectedAdminRoute>
                  <DatasetDefinitionPage />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/data-sources/packs/:id"
              element={
                <ProtectedAdminRoute>
                  <PackPage />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/data-sources/datasets/:name"
              element={
                <ProtectedAdminRoute>
                  <DatasetDetailPage />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/data-sources/metrics/:key"
              element={
                <ProtectedAdminRoute>
                  <MetricDetailPage />
                </ProtectedAdminRoute>
              }
            />
            {/* The old Datasets page is now part of Data sources */}
            <Route path="/admin/datasets" element={<Navigate to="/admin/data-sources?kind=dataset" replace />} />
            <Route
              path="/admin/companies"
              element={
                <ProtectedSuperAdminRoute>
                  <CompanyManagement />
                </ProtectedSuperAdminRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </div>
    </BrowserRouter>
  );
}

export default App;
